import { useMemo } from 'react';
import useQuery from 'src/hooks/useQuery';
import Item from './Item';
import { ApiRoutes } from 'src/lib/routes';
import { Tag, FilterOption, City, Country, FilterTab, PriceRangeItem } from 'src/types';
import useFilter from 'src/hooks/useFilter';
import { uniqBy, sortBy } from 'lodash-es';
import { SORT_OPTIONS, TIME_OF_DAY_OPTIONS, CHILD_AGE_OPTIONS, MasterCurrency } from 'src/lib/constants';
import AllItem from './AllItem';
import useBreakpoints from 'src/hooks/useBreakpoints';
import classNames from 'classnames';
import FilterItemProvider from 'src/contexts/FilterItemProvider';
import useSearchBarState from 'src/hooks/useSearchBarState';
import { generateArrayDates } from 'src/helpers';
import { useTranslations } from 'next-intl';
import useInitialData from 'src/hooks/useInitialData';

const noPriceSortOptions = SORT_OPTIONS.filter(
  (o) => !['asc', 'desc'].includes(String(o.value))
).map((item) => ({ ...item, translationKey: item.label }));

const FilterInner: React.FC<{ queryParams?: Record<string, any>; contentClass: string; notSticky?: boolean }> = ({
  queryParams,
  contentClass,
  notSticky,
}) => {
  const initialData = useInitialData();
  const translation = useTranslations('components.filter.index')
  const constantsTranslation = useTranslations('constants')
  const sortOptions = SORT_OPTIONS.map((item) => ({...item, label: constantsTranslation(item.label)}))
  const timeOfDayOptions = TIME_OF_DAY_OPTIONS.map((item) => ({
    ...item,
    desc: constantsTranslation(`${item.desc}`),
    label: constantsTranslation(item.label),
  }));
  const show = true
  const { isScreenMd } = useBreakpoints();
  const { filterRecord } = useFilter();
  const [searchParams] = useSearchBarState()
  const { data } = useQuery<{
    activityTypes: Tag[];
    locations: { id: number; name: string }[];
    cities: { id: number; name: string }[];
    suitables: { id: number; name: string }[];
    durations: number[];
    minAges: number[];
  }>(
    filterRecord
      ? ApiRoutes.apiFilterItemsRoute(
          {},
          {
            record_id: filterRecord.id,
            record_type: filterRecord.type
          }
        ).toUrl()
      : null
  );
  const { activityTypes, locations, cities, suitables, durations, minAges } = data || {
    activityTypes: [],
    locations: [],
    cities: [],
    suitables: [],
    durations: [],
    minAges: []
  };

  const { data: dataPriceRange } = useQuery<{ priceRange: [PriceRangeItem, PriceRangeItem] }>(
    filterRecord
      ? ApiRoutes.apiFilterPriceRangeRoute(
          {},
          {
            ...(queryParams || {}),
            record_id: filterRecord.id,
            record_type: filterRecord.type,
            start_date: searchParams.dates.length === 2
              ? generateArrayDates(searchParams.dates[0], searchParams.dates[1])
              : searchParams.dates,
            qty: searchParams.numGuest
          }
        ).toUrl()
      : null
  );
  const { priceRange } = dataPriceRange || { priceRange: [{ masterPriceCents: 0, priceCurrency: MasterCurrency }, { masterPriceCents: 0, priceCurrency: MasterCurrency }] }

  const formattedActivityTypes: FilterOption[] = useMemo(
    () =>
      uniqBy(
        activityTypes
          .filter((o) => !o.parentId)
          .concat(activityTypes.filter((o) => !!o.parent).map((o) => o.parent) as Tag[])
          .sort((a, b) => a.name.localeCompare(b.name)),
        'id'
      ).map((tag) => {
        return {
          label: tag.name,
          value: tag.id,
          children: activityTypes
            .filter((o) => o.parentId === tag.id)
            .map((o) => ({ label: o.name, value: o.id })),
        };
      }),
    [JSON.stringify(activityTypes)]
  );

  const formattedDurations: FilterOption[] = useMemo(
    () =>
      sortBy(durations).map((durationDay) => ({
        value: durationDay,
        label: constantsTranslation('pluralizeDurationDay', { durationDay: durationDay }),
      })),
    [JSON.stringify(durations)]
  );

  const formattedChildAges: FilterOption[] = useMemo(
    () =>
      CHILD_AGE_OPTIONS.filter((o) => {
        const [min, max] = String(o.value).split('_').map(Number);
        return minAges.some((age) => age >= min && age <= max);
      }).map((o) => ({ label: constantsTranslation(o.label), value: o.value })),
    [JSON.stringify(minAges)]
  );

  const formattedSuitables: FilterOption[] = useMemo(
    () =>
      (initialData.suitableGroups || []).map((group) => {
        return {
          label: translation(group.name),
          value: group.name,
          children: suitables
            .filter((o) => group.tagIds.includes(Number(o.id)))
            .map((o) => ({ label: o.name, value: o.id })),
        };
      }).filter((o) => o.value === 'family_friendly' && formattedChildAges.length > 0 || o.children && o.children.length > 0),
    [JSON.stringify(suitables), JSON.stringify(initialData.suitableGroups), JSON.stringify(formattedChildAges)]
  );

  const formattedLocations: FilterOption[] = useMemo(
    () => locations.map((o) => ({ label: o.name, value: o.id })),
    [JSON.stringify(locations)]
  );

  const formattedCities: FilterOption[] = useMemo(
    () => cities.map((o) => ({ label: o.name, value: o.id })),
    [JSON.stringify(cities)]
  );

  return filterRecord ? (
    <div className={classNames({ [contentClass]: show })}>
      <div
        className={classNames('container tw-font-styleSans tw-py-3 md:tw-py-4')}
      >
        <div
          className={classNames('tw-flex tw-items-center tw-justify-start tw--mx-2', {
            'tw-invisible': !show,
          })}
        >
          <Item
            inputType="radio"
            label={translation('sortBy')}
            fieldName="sortBy"
            hideCounter={true}
            options={sortOptions}
            showFilter={show}
            notSticky={notSticky}
          />
          {activityTypes.length > 0 ? (
            <Item
              inputType="checkbox"
              fieldName="activityType"
              isNested
              label={translation('activityType')}
              options={formattedActivityTypes}
              showFilter={show}
              notSticky={notSticky}
            />
          ) : null}
          {isScreenMd ? (
            <>
              {cities.length > 1 ? (
                <Item
                  inputType="checkbox"
                  label={translation('location')}
                  fieldName="cityIds"
                  options={formattedCities}
                  showFilter={show}
                  notSticky={notSticky}
                />
              ) : locations.length > 0 ? (
                <Item
                  inputType="checkbox"
                  label={translation('location')}
                  fieldName="locationIds"
                  options={formattedLocations}
                  showFilter={show}
                  notSticky={notSticky}
                />
              ) : null}
              <Item
                inputType="checkbox"
                label={translation('timeOfDay')}
                fieldName="time"
                options={timeOfDayOptions}
                showFilter={show}
                notSticky={notSticky}
              />
            </>
          ) : null}
          <AllItem
            childAges={formattedChildAges}
            durations={formattedDurations}
            suitables={formattedSuitables}
            activityTypes={formattedActivityTypes}
            locations={formattedLocations}
            cities={formattedCities}
            prices={priceRange as [PriceRangeItem, PriceRangeItem]}
            showFilter={show}
            notSticky={notSticky}
          />
        </div>
      </div>
    </div>
  ) : (
    <></>
  );
};

const FilterCollection: React.FC<{ contentClass: string; notSticky?: boolean }> = ({
  contentClass,
  notSticky,
}) => {
  const translation = useTranslations('components.filter.index')
  const constantsTranslation = useTranslations('constants')

  return (
    <div className={contentClass}>
      <div
        className={classNames('container tw-font-styleSans tw-py-3 md:tw-py-4 tw-px-2 md:tw-px-4')}
      >
        <div className={classNames('tw-flex tw-items-center tw-justify-start tw--mx-2')}>
          <Item
            inputType="radio"
            label={translation('sortBy')}
            fieldName="sortBy"
            hideCounter={true}
            options={noPriceSortOptions.map((o) => ({...o, label: constantsTranslation(o.translationKey)}))}
            showFilter={true}
            notSticky={notSticky}
          />
        </div>
      </div>
    </div>
  );
};

const FilterBlog: React.FC<{ contentClass: string; notSticky?: boolean }> = ({
  contentClass,
  notSticky,
}) => {
  const translation = useTranslations('components.filter.index')
  const constantsTranslation = useTranslations('constants')
  const { data } = useQuery<{ data: { topics: Tag[]; cities: City[]; countries: Country[] } }>(
    ApiRoutes.apiBlogFilterItemsRoute({}).toUrl()
  );
  const { data: data2 } = data || {};
  const { topics, cities, countries } = data2 || {}

  const topicTagOptions: FilterOption[] = topics
    ? topics.map((tag) => {
        return {
          label: tag.name,
          value: tag.id,
        };
      })
    : [];
  const cityOptions: FilterOption[] = countries
    ? countries.map((country) => {
        return {
          label: country.name,
          value: `country_${country.id}`,
          children: cities
            ? cities
                .filter((o) => o.countryId === country.id)
                .map((city) => ({
                  label: city.name,
                  value: city.id,
                }))
            : undefined,
        };
      })
    : [];

  return (
    <div className={contentClass}>
      <div
        className={classNames('container tw-font-styleSans tw-py-3 md:tw-py-4 tw-px-2 md:tw-px-4')}
      >
        <div className={classNames('tw-flex tw-items-center tw-justify-start tw--mx-2')}>
          <Item
            inputType="radio"
            label={translation('sortBy')}
            fieldName="sortBy"
            hideCounter={true}
            options={noPriceSortOptions.map((o) => ({...o, label: constantsTranslation(o.translationKey)}))}
            showFilter={true}
            notSticky={notSticky}
          />
          <Item
            inputType="checkbox"
            label={translation('category')}
            fieldName="topicTagIds"
            options={topicTagOptions}
            showFilter={true}
            notSticky={notSticky}
          />
          <Item
            inputType="checkbox"
            label={translation('city')}
            fieldName="cityIds"
            isNested
            options={cityOptions}
            showFilter={true}
            notSticky={notSticky}
          />
        </div>
      </div>
    </div>
  );
};

const Filter: React.FC<{
  contentClass?: string;
  notSticky?: boolean;
  tab?: FilterTab;
  queryParams?: Record<string, any>;
}> = ({ queryParams, contentClass, notSticky, tab = 'option' }) => {
  return (
    <FilterItemProvider>
      {tab === 'option' ? (
        <FilterInner queryParams={queryParams} contentClass={contentClass || 'tw-bg-brand-yellow'} notSticky={notSticky} />
      ) : tab === 'collection' ? (
        <FilterCollection
          contentClass={contentClass || 'tw-bg-brand-yellow'}
          notSticky={notSticky}
        />
      ) : tab === 'blog' ? (
        <FilterBlog contentClass={contentClass || 'tw-bg-brand-yellow'} notSticky={notSticky} />
      ) : null}
    </FilterItemProvider>
  );
};

export default Filter;
